import React, { useEffect } from "react";
import { Outlet, useParams, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet-async";
import { supportedLngs } from "../i18n";

const langToLocale = {
  en: "en_US",
  es: "es_ES",
  de: "de_DE",
  ja: "ja_JP",
  fr: "fr_FR",
  pt: "pt_BR",
  ru: "ru_RU",
  it: "it_IT",
  nl: "nl_NL",
  pl: "pl_PL",
};

export default function LanguageWrapper() {
  const { lang } = useParams();
  const { i18n, t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (lang && supportedLngs.includes(lang)) {
      if (i18n.language !== lang) {
        i18n.changeLanguage(lang);
      }
      document.documentElement.lang = lang;
    } else if (lang && !supportedLngs.includes(lang)) {
      // Invalid language code — redirect to English with the same path
      const rest = location.pathname.replace(`/${lang}`, "") || "/";
      navigate(`/en${rest === "/" ? "" : rest}`, { replace: true });
    } else {
      // No lang prefix — detect or default to English
      const detected = i18n.language?.split("-")[0] || "en";
      const targetLang = supportedLngs.includes(detected) ? detected : "en";
      if (i18n.language !== targetLang) {
        i18n.changeLanguage(targetLang);
      }
      document.documentElement.lang = targetLang;
    }
  }, [lang, i18n, navigate, location.pathname]);

  const currentLang = lang && supportedLngs.includes(lang) ? lang : "en";
  const pathWithoutLang = location.pathname
    .replace(new RegExp(`^/${currentLang}`), "")
    .replace(/^$/, "/");

  const baseUrl = "https://trackteam.app";

  return (
    <>
      <Helmet>
        <html lang={currentLang} />
        <title>{t("meta.title")}</title>
        <meta name="description" content={t("meta.description")} />
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="TrackTeam" />
        <meta property="og:title" content={t("meta.title")} />
        <meta property="og:description" content={t("meta.description")} />
        <meta property="og:locale" content={langToLocale[currentLang] || "en_US"} />
        <meta property="og:image" content={`${baseUrl}/TrackTeam/trackteamlogo.png`} />
        <meta property="og:url" content={`${baseUrl}${location.pathname}`} />
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={t("meta.title")} />
        <meta name="twitter:description" content={t("meta.description")} />
        <meta name="twitter:image" content={`${baseUrl}/TrackTeam/trackteamlogo.png`} />
        {/* Canonical */}
        <link rel="canonical" href={`${baseUrl}${location.pathname}`} />
        {/* Hreflang */}
        {supportedLngs.map((lng) => (
          <link
            key={lng}
            rel="alternate"
            hrefLang={lng}
            href={`${baseUrl}/${lng}${pathWithoutLang === "/" ? "" : pathWithoutLang}`}
          />
        ))}
        <link
          rel="alternate"
          hrefLang="x-default"
          href={`${baseUrl}/en${pathWithoutLang === "/" ? "" : pathWithoutLang}`}
        />
      </Helmet>
      <Outlet />
    </>
  );
}
